import { useState, type JSX } from 'react';
import { Link } from 'react-router';
import { authApi } from '../api/client.js';
import { userErrorMessage } from '../api/errors.js';
import { photoConsentApi } from '../api/library.js';
import { useAuth } from '../auth/context.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';

/**
 * Явный чекбокс согласия на обработку фото при первой загрузке (01-SPEC.md §5.5).
 * Без него загрузки отвечают `photo_consent_required`, поэтому галочка стоит
 * рядом с самой загрузкой, а не спрятана в настройках.
 *
 * The consent is the account's, not the card's. Once given, a ticked box on every photo
 * form read as consent for these photos, and unticking it on one card silently blocked
 * uploads everywhere — so beside the photos it becomes one line, and taking it back
 * lives in Settings (`settings`), where its account-wide reach is no surprise.
 */
export function PhotoConsent({
  settings = false,
}: { readonly settings?: boolean } = {}): JSX.Element {
  const { t } = useI18n();
  const legal = useLegal();
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accepted = user?.consentPhotoProcessing ?? false;

  if (accepted && !settings) {
    return (
      <p className="text-sm text-[var(--kitup-color-muted)]">
        {t.library.consent.given}{' '}
        <Link className="underline" to={legal.routes.photoConsent} target="_blank">
          {t.library.consent.documentLink}
        </Link>
        {' · '}
        <Link className="underline" to="/app/settings">
          {t.library.consent.manage}
        </Link>
      </p>
    );
  }

  return (
    <div className="rounded-[var(--kitup-radius-sm)] bg-[var(--kitup-color-accent)]/5 p-3">
      <label className="flex items-start gap-3 text-sm font-medium">
        <input
          type="checkbox"
          className="mt-1"
          checked={accepted}
          disabled={saving}
          onChange={(event) => {
            const next = event.target.checked;
            setSaving(true);
            setError(null);
            void photoConsentApi
              .set(
                next ? { accepted: true, version: legal.documentVersionId } : { accepted: false },
              )
              .then(() => authApi.me())
              .then((response) => setUser(response.user))
              .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
              .finally(() => setSaving(false));
          }}
        />
        <span>
          {t.library.consent.label}{' '}
          <Link className="underline" to={legal.routes.photoConsent} target="_blank">
            {t.library.consent.documentLink}
          </Link>
          <span className="mt-1 block text-xs font-normal text-[var(--kitup-color-muted)]">
            {t.library.consent.hint}
          </span>
        </span>
      </label>
      {/*
        The reason the uploads below are disabled sits with the box that enables them —
        under the slots it read as a stray warning far from anything it was about.
      */}
      {!accepted && !error && !settings ? (
        <p role="status" className="mt-2 pl-7 text-sm text-amber-800">
          {t.library.consent.required}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
